import React, { useMemo } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useCollectionBinding } from '@vibesites/runtime/react';
import { RichTextContent } from '@vibesites/runtime/react';
import { useScrollReveal, useStaggerReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { useComfyCtaStyles } from '../hooks/useComfyCtaStyles';

interface Floorplan {
  floorplan_id?: string | null;
  floorplan_name?: string | null;
  floorplan_image_url?: string | null;
  floorplan_beds_min?: string | null;
  floorplan_beds_max?: string | null;
  floorplan_baths_min?: string | null;
  floorplan_sqft_min?: string | null;
  floorplan_sqft_max?: string | null;
  floorplan_price_min?: string | null;
  floorplan_price_max?: string | null;
  floorplan_available_units?: string | null;
}

// ============================================================================
// Bedroom category helpers
// ============================================================================

interface BedroomCategory {
  key: string;
  label: string;
  digit: string;
  param: string;
}

const BED_ORDER = ['studio', 'one', 'two', 'three', 'four', 'five'];

function bedCountToKey(bedsRaw: string | null | undefined): string {
  if (!bedsRaw) return 'studio';
  const trimmed = bedsRaw.trim().toLowerCase();
  if (trimmed === '0' || trimmed === 'studio' || trimmed === 'std') return 'studio';
  const num = parseInt(trimmed, 10);
  if (isNaN(num) || num <= 0) return 'studio';
  const names = ['studio', 'one', 'two', 'three', 'four', 'five'];
  return names[num] || `${num}`;
}

const WORD_NUMBERS: Record<string, string> = {
  studio: 'Studio',
  one: 'One',
  two: 'Two',
  three: 'Three',
  four: 'Four',
  five: 'Five',
};

/** Word-based category label, e.g. "One Bedroom", "Two Bedrooms", "Studio". */
function keyToWordLabel(key: string): string {
  if (key === 'studio') return 'Studio';
  const word = WORD_NUMBERS[key] || key;
  const noun = key === 'one' ? 'Bedroom' : 'Bedrooms';
  return `${word} ${noun}`;
}

/** Big display digit — "S" for studio, otherwise the bedroom count. */
function keyToDigit(key: string): string {
  if (key === 'studio') return 'S';
  const idx = BED_ORDER.indexOf(key);
  return idx > 0 ? String(idx) : key;
}

function groupFloorplansByBeds(floorplans: Floorplan[]): BedroomCategory[] {
  const groups: Record<string, Floorplan[]> = {};

  for (const plan of floorplans) {
    const key = bedCountToKey(plan.floorplan_beds_min);
    if (!groups[key]) groups[key] = [];
    groups[key].push(plan);
  }

  const sorted = Object.keys(groups).sort((a, b) => {
    const ai = BED_ORDER.indexOf(a);
    const bi = BED_ORDER.indexOf(b);
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });

  return sorted.map((key) => ({
    key,
    label: keyToWordLabel(key),
    digit: keyToDigit(key),
    param: key,
  }));
}

// ============================================================================
// Category column sub-component
// ============================================================================

function CategoryColumn({ cat }: { cat: BedroomCategory }) {
  return (
    <a
      data-reveal
      href={`/floor-plans?bedroom=${cat.param}`}
      className="reveal-fade-up fp-col group flex flex-col items-center text-center no-underline"
    >
      <span className="font-heading text-7xl sm:text-8xl md:text-9xl font-bold leading-none text-[var(--vibe-color-text)] transition-transform duration-300 group-hover:-translate-y-1">
        {cat.digit}
      </span>
      <span className="fp-col-label mt-6 pb-3 font-body text-lg sm:text-xl font-bold uppercase tracking-wide text-[var(--vibe-color-text)] whitespace-nowrap">
        {cat.label}
      </span>
    </a>
  );
}

// ============================================================================
// Component
// ============================================================================

export function FloorplansList() {
  const floorplans = useCollectionBinding<Floorplan>('floorplanslist.items', {
    collection: 'floorplans',
  });

  const categories = useMemo(
    () => (floorplans?.length ? groupFloorplansByBeds(floorplans) : []),
    [floorplans],
  );

  useRevealStyles();
  useComfyCtaStyles();
  const headerRef = useScrollReveal<HTMLDivElement>();
  const gridRef = useStaggerReveal<HTMLDivElement>({ staggerMs: 120 });

  const hasCategories = categories.length > 0;

  return (
    <section className="py-16 md:py-24 bg-[var(--vibe-color-background)]">
      <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-8">
        <div ref={headerRef} className="reveal-fade-up text-center mb-12 md:mb-20">
          <RichTextContent
            path="floorplanslist.title"
            label="Title"
            as="h2"
            className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold uppercase text-[var(--vibe-color-text)] mb-5 leading-tight tracking-wide"
          />
          <RichTextContent
            path="floorplanslist.subtitle"
            label="Subtitle"
            as="p"
            className="font-body text-base sm:text-lg text-[var(--vibe-color-text)] max-w-2xl mx-auto leading-relaxed [&_a]:text-[var(--vibe-color-text)] [&_a]:underline [&_a]:decoration-[var(--vibe-color-primary)] [&_a]:underline-offset-2 [&_a:hover]:decoration-2"
          />
        </div>

        {hasCategories ? (
          <>
            <div
              ref={gridRef}
              className={`flex flex-col sm:flex-row items-center justify-center gap-y-10 sm:gap-x-8 sm:gap-y-12 md:gap-x-24 lg:gap-x-40 mb-16 md:mb-20 ${
                categories.length >= 4 ? 'sm:flex-wrap' : 'sm:flex-nowrap'
              }`}
            >
              {categories.map((cat) => (
                <CategoryColumn key={cat.key} cat={cat} />
              ))}
            </div>

            <div className="flex justify-center">
              <a
                data-reveal
                href="/floor-plans"
                className="reveal-fade-up comfy-cta"
              >
                View All Layouts
              </a>
            </div>
          </>
        ) : (
          <div className="text-center py-12 text-[var(--vibe-color-text)]/60">
            <p className="font-body text-lg">Floor plans will be displayed here.</p>
          </div>
        )}
      </div>

      <style>{`
        .fp-col-label {
          position: relative;
        }
        /* Accent bar beneath each category label — fixed width so
           all columns (including short labels like "STUDIO") share
           the same visual weight regardless of text length. */
        .fp-col-label::after {
          content: '';
          position: absolute;
          left: 50%;
          bottom: 0;
          transform: translateX(-50%);
          width: 5rem;
          height: 4px;
          background: var(--vibe-color-secondary);
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'FloorplansList',
  displayName: 'Floor Plans List',
  tokens: {
    'floorplanslist.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>Make Yourself at Home</p>',
    },
    'floorplanslist.subtitle': {
      label: 'Subtitle',
      type: 'richtext',
      defaultValue:
        '<p>Please select your desired layout below or browse our <a href="/gallery">photo gallery</a>.</p>',
    },
  },
  collectionBindings: {
    'floorplanslist.items': {
      label: 'Floor Plans',
      collection: 'floorplans',
    },
  },
};

(FloorplansList as any).contentSchema = contentSchema;

export default FloorplansList;
